import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Paperclip, X, FileText, Download, Loader2, Image as ImageIcon } from 'lucide-react';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const resolveAttachmentUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// Displays a saved attachment (image thumbnail or file chip with download)
export const AttachmentPreview = ({ url, name, type, testid }) => {
  const [retry, setRetry] = useState(0);
  const [failed, setFailed] = useState(false);
  if (!url) return null;
  const full = resolveAttachmentUrl(url);
  if (type === 'image') {
    // The S3 provider can transiently 503; retry the image a couple of times with a cache-buster.
    const src = retry > 0 ? `${full}${full.includes('?') ? '&' : '?'}r=${retry}` : full;
    if (failed) {
      return (
        <a href={full} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-background hover:bg-muted transition-colors text-sm" data-testid={testid}>
          <ImageIcon className="w-4 h-4 text-[#C85A42] shrink-0" />
          <span className="truncate">{name || 'View image'}</span>
          <Download className="w-4 h-4 text-muted-foreground shrink-0" />
        </a>
      );
    }
    return (
      <a href={full} target="_blank" rel="noopener noreferrer" className="block mt-2" data-testid={testid}>
        <img
          src={src}
          alt={name || 'attachment'}
          className="max-h-48 rounded-lg border object-cover hover:opacity-90 transition-opacity"
          onError={() => { if (retry < 2) { setTimeout(() => setRetry((r) => r + 1), 600); } else { setFailed(true); } }}
        />
      </a>
    );
  }
  return (
    <a
      href={full}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-background hover:bg-muted transition-colors text-sm max-w-full"
      data-testid={testid}
    >
      <FileText className="w-4 h-4 text-[#C85A42] shrink-0" />
      <span className="truncate">{name || 'Download file'}</span>
      <Download className="w-4 h-4 text-muted-foreground shrink-0" />
    </a>
  );
};

// Upload control + selected-attachment chip. `attachment` is { attachment_url, attachment_name, attachment_type } | null
export const AttachmentUploader = ({ attachment, onChange, uploadFn, disabled, testidPrefix = 'support' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await uploadFn(formData);
      // Normalize: local uploads return '/api/uploads/..'; S3 returns a bare key
      // ('attachments/xx.jpg') which the shared /api/uploads/{path} route resolves via presigned redirect.
      let url = data.url;
      if (url && !url.startsWith('http') && !url.startsWith('/')) {
        url = `/api/uploads/${url}`;
      }
      onChange({
        attachment_url: url,
        attachment_name: data.original_name || data.filename,
        attachment_type: data.category === 'image' ? 'image' : 'document',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (attachment?.attachment_url) {
    const isImg = attachment.attachment_type === 'image';
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/40 text-sm" data-testid={`${testidPrefix}-attachment-chip`}>
        {isImg ? <ImageIcon className="w-4 h-4 text-[#C85A42]" /> : <FileText className="w-4 h-4 text-[#C85A42]" />}
        <span className="truncate max-w-[200px]">{attachment.attachment_name}</span>
        <button type="button" onClick={() => onChange(null)} className="text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-attachment-remove`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleFile} className="hidden" data-testid={`${testidPrefix}-attachment-input`} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        data-testid={`${testidPrefix}-attachment-btn`}
      >
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
        Attach
      </Button>
    </>
  );
};
